import type { FC, TargetedEvent } from "preact/compat";
import type { ViewProps } from "./interfaces";
import { Progress } from "~/components";
import { useSignal } from "@preact/signals";
import UploadFileIcon from "@material-design-icons/svg/outlined/upload_file.svg";
import FilePresentIcon from "@material-design-icons/svg/outlined/file_present.svg";
import { useAutoResize } from "~/hooks";
import Comments from "./Comments";

const View: FC<ViewProps> = ({ task, onRefresh }) => {
    const descriptionRef = useAutoResize();
    const completeness = useSignal(task?.progress ?? 0);
    const name = useSignal(task?.name ?? "");
    const description = useSignal(task?.description ?? "");
    const attachments = useSignal(task?.attachments ?? []);

    const update = () => {
        if (task) {
            task.progress = completeness.value;
            task.name = name.value;
            task.description = description.value;
            task.attachments = attachments.value;
        }
        onRefresh();
    };

    const handleUpload = (event: TargetedEvent<HTMLInputElement, Event>) => {
        const files = event.currentTarget.files;

        if (!files) {
            return;
        }

        let id = attachments.peek().length;

        attachments.value = attachments.value.concat(
            Array.from(files, (file) => ({
                id: String(id++),
                url: "",
                name: file.name,
                type: file.type,
            }))
        );

        event.currentTarget.value = "";
    };

    return (
        <div className="w-full pr-16">
            <h4 className="mb-2 text-sky-700">Title</h4>
            <input
                className="w-full p-1 -ml-1 text-xl font-bold rounded outline-none hover:bg-zinc-100 focus:ring focus:ring-sky-500"
                placeholder="Name..."
                value={name.value}
                onInput={({ currentTarget }) =>
                    (name.value = currentTarget.value)
                }
            />
            <label className="flex flex-col mt-6">
                <h4 className="mb-2 text-sky-700">Description</h4>
                <textarea
                    ref={descriptionRef}
                    className="p-2 min-h-[120px] border border-gray-300 rounded outline-none resize-none focus:ring focus:ring-sky-500"
                    placeholder="Add a description..."
                    value={description.value}
                    onInput={({ currentTarget }) =>
                        (description.value = currentTarget.value)
                    }
                />
            </label>
            <div className="flex flex-col mt-6">
                <h4 className="mb-2 text-sky-700">Attachments</h4>
                <div className="mb-2">
                    <label className="relative flex p-4 w-[260px] font-bold text-gray-400 rounded cursor-pointer group hover:text-sky-500">
                        <input
                            type="file"
                            className="absolute w-0 h-0 opacity-0 peer"
                            onChange={handleUpload}
                        />
                        <div className="absolute inset-0 border border-gray-300 border-dashed rounded group-hover:border-sky-500 peer-focus:ring peer-focus:ring-sky-500" />
                        <div className="flex items-center">
                            <UploadFileIcon
                                width="24"
                                className="mr-1 fill-current"
                            />
                            <span className="ml-2">Add attachment</span>
                        </div>
                    </label>
                </div>
                <div className="flex flex-wrap">
                    {attachments.value.map((attachment) => (
                        <div
                            key={attachment.id}
                            className="p-2 text-gray-500 border border-gray-300 rounded w-[90px] flex flex-col fill-current mr-2 mb-2 text-center"
                        >
                            <FilePresentIcon
                                width="36"
                                height="36"
                                className="mx-auto"
                            />
                            <span className="block mt-4 text-xs truncate">
                                {attachment.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="w-full mt-4 rounded">
                <h4 className="mb-3 cursor-default text-sky-700">
                    Completeness
                </h4>
                <Progress
                    value={completeness.value}
                    onChange={(value) => {
                        completeness.value = value;

                        update();
                    }}
                    ticks
                />
            </div>
            {task && (
                <div className="w-full mt-4">
                    <h4 className="mb-2 cursor-default text-sky-700">
                        Comments
                    </h4>
                    <Comments task={task} />
                </div>
            )}
        </div>
    );
};

export default View;
